// import { Flex } from '@chakra-ui/react';
// import { useState } from 'react';
import { ModalWithForm } from '@/components/ats/modal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { API_SERVICE } from '../api/api';
import { useHistory, useParams } from 'react-router-dom';
import { CreateEditCRModalProps } from '../types/customReport';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { useState } from 'react';
import { reportUtils } from '@/pages/reports/reports-main/utils/reportUtils';

export const CreateEditCustomReportModal = ({
  report,
  onClose,
  modalTitle,
  submitButtonLabel,
  type,
  onSubmit,
  onSuccess,
  skipCTADisabled = false,
}: CreateEditCRModalProps) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { reportID } = useParams();
  const toast = useShowToast();
  const { feReportsV3 } = useKFlags();
  const [isCtaLoading, setIsCtaLoading] = useState(false);

  const formConfig = [
    {
      name: 'name',
      label: 'Name',
      type: 'input',
      maxLength: 200,
      isRequired: true,
      placeholder: 'Add name here',
      autoFocus: true,
      isInputSubmit: true,
    },
    {
      name: 'description',
      type: 'textArea',
      label: 'Description',
      maxLength: 250,
      placeholder: 'Add your description here',
    },
  ];

  // Mutations
  const createCRMutation = useMutation({
    mutationFn: API_SERVICE.createCustomReport,
    onSuccess: (data) => {
      showToast('Report created');
      // don't redirect if type is cloneStaticReport, since we have to add the blocks and then redirect
      if (type !== 'cloneStaticReport') {
        const redirectUrl = reportUtils.getCustomReportUrl(
          data?.id,
          data?.name,
          feReportsV3
        );
        history.push(redirectUrl);
      }
      onSuccess?.({ data, setIsCtaLoading });
    },
  });

  const cloneCRMutation = useMutation({
    mutationFn: (data) =>
      API_SERVICE.cloneCustomReport(data['reportID'], data['payload']),
    onSuccess: (data) => {
      showToast('Report cloned');
      onClose();
      const redirectUrl = reportUtils.getCustomReportUrl(
        data?.id,
        data?.name,
        feReportsV3
      );
      history.push(redirectUrl);
    },
  });

  const updateCRMutation = useMutation({
    mutationFn: (data) =>
      API_SERVICE.updateCustomReport(data['reportID'], data['payload']),
    onSuccess: (updatedData) => {
      showToast('Report updated');

      if (feReportsV3) {
        // Update the title locally without touching the cache
        // This prevents resetting the report body while showing the new title
        const reportContainer = document.querySelector(
          '[data-report-container]'
        );
        if (reportContainer) {
          const titleUpdateEvent = new CustomEvent('title-updated', {
            detail: { newTitle: updatedData.name },
          });
          reportContainer.dispatchEvent(titleUpdateEvent);
        }
      } else {
        // Use the response data directly to update the cache
        queryClient.setQueryData([`getCustomReports/${reportID}`], {
          data: updatedData,
          status: 'success',
        });
      }
      onSubmit(updatedData);
    },
  });

  const showToast = (title) => {
    toast({
      title,
    });
  };

  const formSubmit = (data) => {
    let reportID = report?.id;
    if (type === 'clone') {
      cloneCRMutation.mutate({ reportID, payload: data } as any);
    } else if (type === 'edit') {
      updateCRMutation.mutate({ reportID, payload: data } as any);
    } else {
      createCRMutation.mutateAsync(data);
    }
  };

  const initiateOnClose = () => {
    onClose();
  };

  const getReportName = () => {
    let name = report?.name;
    if (type === 'clone' || type === 'cloneStaticReport') {
      name = report?.name ? `${report.name} - copy` : '';
    }
    return name;
  };

  return (
    <ModalWithForm
      isOpen={true}
      showCloseButton={false}
      autoFocus={true}
      modalHeaderProps={{ mt: '0' }}
      initialValues={{
        name: getReportName(),
        description: report?.description || '',
      }}
      onClose={initiateOnClose}
      formConfig={formConfig}
      primaryCtaText={submitButtonLabel || 'Create report'}
      isCtaLoading={
        createCRMutation.isPending ||
        cloneCRMutation.isPending ||
        updateCRMutation.isPending ||
        isCtaLoading
      }
      headerText={modalTitle?.headerText}
      subHeaderText={modalTitle?.subHeaderText}
      handleSubmit={formSubmit}
      skipCTADisabled={skipCTADisabled}
    />
  );
};
